import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { ParkingLocation } from '../../types/parkingLocation';
import { useParkingLocations } from '../../hooks/useParkingLocations';
import {
	readScanLastSelection,
	writeScanLastSelection,
} from '../../utils/scanLastSelection';

interface ParkingLocationSelectorProps {
	activeParkingLocation: ParkingLocation | null;
	setActiveParkingLocation: React.Dispatch<
		React.SetStateAction<ParkingLocation | null>
	>;
	selectedCustomerId: string | null;
	isCameraOn: boolean;
}

const ParkingLocationSelector: React.FC<ParkingLocationSelectorProps> = (
	props,
) => {
	const { activeParkingLocation, setActiveParkingLocation, isCameraOn } =
		props;
	const { parkingLocations, loading: loadingParkingLocations } =
		useParkingLocations(props.selectedCustomerId);

	// Restore the last-used parking lot once options arrive, but only if the
	// stored lot belongs to the currently selected customer.
	useEffect(() => {
		if (
			activeParkingLocation ||
			parkingLocations.length === 0 ||
			!props.selectedCustomerId
		) {
			return;
		}
		const stored = readScanLastSelection();
		if (
			stored.customerId === props.selectedCustomerId &&
			stored.parkingLocationId
		) {
			const match = parkingLocations.find(
				(l) => l.id === stored.parkingLocationId,
			);
			if (match) setActiveParkingLocation(match);
		}
	}, [parkingLocations, props.selectedCustomerId]);

	return (
		<Autocomplete
			options={parkingLocations}
			getOptionLabel={(option) => option.name}
			value={activeParkingLocation}
			onChange={(event, newValue) => {
				setActiveParkingLocation(newValue);
				if (newValue) {
					writeScanLastSelection({
						parkingLocationId: newValue.id,
					});
				}
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Select Parking Location"
					fullWidth
				/>
			)}
			disabled={isCameraOn}
			loading={loadingParkingLocations}
			loadingText="Loading..."
			renderOption={(props, option) => {
				const { key, ...restProps } = props;
				return (
					<li key={option.id} {...restProps}>
						<Box display="flex" flexDirection="column">
							<Typography variant="body1">
								{option.name}
							</Typography>
						</Box>
					</li>
				);
			}}
			fullWidth
		/>
	);
};

export default ParkingLocationSelector;
